/**
 * Circle context of the open workspace (spec §9.29): is it shared, who is in it, who am I here.
 * The roster is written into `settings.members` by the server when membership changes, so every
 * replica — including offline ones and the kiosk — can compute turns and `mine` without an API call.
 */
import type { LoroDoc } from "loro-crdt";
import { C } from "../core/schema";
import { useDocSelector } from "../core/useDocSelector";
import type { DocHandle } from "../sync/docManager";
import { useSession } from "../app/session";

export interface Member {
  id: string;
  name: string;
  color: string | null;
  kind: "person" | "kid" | "viewer";
  role: "owner" | "member" | "viewer";
}

export interface CircleCtx {
  shared: boolean;
  members: Member[];
  memberIds: string[];
  me: string | null;
  myKind: Member["kind"];
  nameOf(id: string | null | undefined): string;
}

export function readCircle(doc: LoroDoc): { shared: boolean; members: Member[] } {
  const s = doc.getMap(C.settings).toJSON() as { shared?: boolean; members?: Member[] };
  return { shared: s.shared === true, members: Array.isArray(s.members) ? s.members : [] };
}

export function useCircle(handle: DocHandle | null): CircleCtx {
  const data = useDocSelector(handle, (doc) => readCircle(doc));
  const profile = useSession((s) => s.session?.profile);
  const members = data?.members ?? [];
  return {
    shared: data?.shared ?? false,
    members,
    memberIds: members.filter((m) => m.kind !== "viewer").map((m) => m.id),
    me: profile?.id ?? null,
    myKind: (profile?.kind as Member["kind"]) ?? "person",
    nameOf: (id) => members.find((m) => m.id === id)?.name ?? "",
  };
}
